"use client"

import type React from "react"
// import Image from 'next/image'

import { useState, useRef, useEffect } from "react"
import {
  Search,
  MessageSquare,
  Send,
  UserSearch,
  FileBarChart,
  Calculator,
  FileCode,
  BarChart2,
  Heart,
  DollarSign,
  PieChart,
  Type,
  Tag,
  MessageCircle,
  ThumbsUp,
  Hash,
  Building,
  X,
  FileText,
  TrendingUp,
  Star,
  Users,
  ChevronUp,
} from "lucide-react"
import { Input } from "@/components/ui/input"
import { Avatar } from "@/components/ui/avatar"
import { Checkbox } from "@/components/ui/checkbox"
import { DemoWrapper } from '../DemoWrapper'

// Mock responses for each command
const MOCK_RESPONSES = {
  "Analyze Customer Need": `Based on the selected data, here's my analysis of customer needs:

1. Quality Assurance Automation
- Current manual QA process is inefficient
- Need for real-time monitoring and feedback
- Desire for automated compliance checks

2. Scalability Requirements
- Current system only analyzes 20% of calls
- Need for comprehensive coverage
- Requirement for automated scoring

3. Key Pain Points
- Manual audit processes
- Delayed compliance flagging
- Limited real-time coaching

Recommendation: Focus on implementing automated QA processes with real-time feedback capabilities.`,

  "Build a Sentiment Report": `Generating Sentiment Analysis Report...

Overall Sentiment Distribution:
- Negative: 65%
- Neutral: 35%
- Positive: 0%

Key Findings:
1. High negative sentiment around QA processes
2. Neutral responses primarily in training discussions
3. Implementation concerns across multiple customers

Critical Areas:
- Quality Assurance (Most mentioned)
- Compliance Monitoring
- Training Implementation`,

  "Build ROI Case": `Value Proposition: Eliminating Costs & Driving Retention
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Customer Value:
Enterprises with 500–2000 agents face $1.5M in annual costs due to inefficiencies in QA, compliance risks, training gaps, and system performance issues. Our solution automates QA, reducing review time by 75%, and cuts compliance monitoring from 24 hours to under 1 hour.

Business Value:
This not only eliminates costly inefficiencies but also strengthens customer relationships, extending retention by four years and securing an additional $600K in revenue. More than just savings, this is a long-term investment in growth and operational excellence.`,

  "Draft PRD": `Product Requirements Document - Draft

1. Problem Statement
- Manual QA processes are inefficient
- Limited real-time monitoring capabilities
- Compliance risks due to delayed flagging

2. Proposed Solution
- Automated QA system with real-time analysis
- AI-powered sentiment analysis
- Integrated compliance monitoring

3. Key Features
- Real-time call analysis
- Automated scoring system
- Compliance alert dashboard
- Training recommendation engine

4. Success Metrics
- 90% reduction in QA time
- 100% call coverage
- < 5 minute compliance alert time`,
}

// Update the avatar constants to use the Strive logomark
const assistantAvatar = '/strive_logomark_512.svg'
const userAvatar = '/strive_logomark_512.svg' // You might want a different image for user

export default function TableChat() {
  const [searchQuery, setSearchQuery] = useState("")
  const [isStriveOpen, setIsStriveOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [promptInput, setPromptInput] = useState("")
  const [showSlashMenu, setShowSlashMenu] = useState(false)
  const [isMenuFading, setIsMenuFading] = useState(false)
  const [aiResponse, setAiResponse] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [showSlashCommands, setShowSlashCommands] = useState(false)
  const [selectedCommand, setSelectedCommand] = useState<string | null>(null)

  // Set default sort to Classification A-Z
  const [sortColumn, setSortColumn] = useState<string>("Classification")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  // Resizable panel state
  const [panelWidth, setPanelWidth] = useState(600)
  const [windowWidth, setWindowWidth] = useState(1200) // Default fallback value

  // Update window width on client side only
  useEffect(() => {
    // Only run this code on the client side
    if (typeof window !== "undefined") {
      // Set initial window width
      setWindowWidth(window.innerWidth)

      // Update window width on resize
      const handleResize = () => {
        const newWidth = window.innerWidth
        setWindowWidth(newWidth)

        // If panel is wider than 90% of screen, adjust it
        if (panelWidth > newWidth * 0.9) {
          setPanelWidth(Math.max(300, Math.min(newWidth * 0.9, 600)))
        }

        // On very small screens, ensure panel takes full width
        if (newWidth < 640 && isStriveOpen) {
          setPanelWidth(newWidth)
        }
      }

      window.addEventListener("resize", handleResize)

      // Initial check
      handleResize()

      return () => window.removeEventListener("resize", handleResize)
    }
  }, [panelWidth, isStriveOpen])

  const [isResizing, setIsResizing] = useState(false)

  // First, add state for column widths
  const [columnWidths, setColumnWidths] = useState({
    checkbox: 40,
    title: 200,
    classification: 180,
    statement: 220,
    sentiment: 120,
    score: 120,
    customer: 150,
  })

  // Add state for tracking which column is being resized
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)

  // Handle resize start
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  // Add column resize handlers
  const startColumnResize = (e: React.MouseEvent, column: string) => {
    e.preventDefault()
    e.stopPropagation()
    setResizingColumn(column)
  }

  // Handle resize move and end
  useEffect(() => {
    // Only run this code on the client side
    if (typeof window === "undefined") return

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = windowWidth - e.clientX

      // Set min and max constraints
      const minWidth = 300
      const maxWidth = Math.min(1000, windowWidth * 0.8)

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setPanelWidth(newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = "ew-resize"
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
    }
  }, [isResizing, windowWidth])

  // Add useEffect for column resizing
  useEffect(() => {
    // Only run this code on the client side
    if (typeof window === "undefined") return

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingColumn) return

      const delta = e.movementX
      if (delta === 0) return

      setColumnWidths((prev) => {
        // Increase minimum width to 100px for better readability with ellipsis
        const newWidth = Math.max(100, prev[resizingColumn as keyof typeof prev] + delta)
        return {
          ...prev,
          [resizingColumn]: newWidth,
        }
      })
    }

    const handleMouseUp = () => {
      setResizingColumn(null)
    }

    if (resizingColumn) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = "col-resize"
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      if (resizingColumn) document.body.style.cursor = ""
    }
  }, [resizingColumn])

  const handleSlashCommand = async (command: string, demoMode = false) => {
    setPromptInput("")
    setSelectedCommand(command)
    setAiResponse("")
    
    // If in demo mode, ensure nothing gets focused
    if (demoMode) {
      blurActiveElement();
    }
    
    // Only start fade animation if not in demo mode
    if (!demoMode) {
      setTimeout(() => {
        setIsMenuFading(true)
        
        // After 100ms fade, hide the menu
        setTimeout(() => {
          setShowSlashMenu(false)
          setIsMenuFading(false)
        }, 100)
      }, 2000)
    }

    // Only focus if this is not part of an automated demo 
    if (inputRef.current && !isDemoRunning && !demoMode) {
      inputRef.current.focus()
    }

    // Simulate AI response
    const response = MOCK_RESPONSES[command as keyof typeof MOCK_RESPONSES]
    if (response) {
      await simulateTyping(response)
    }
  }

  const simulateTyping = async (text: string) => {
    setIsTyping(true)
    setAiResponse("")

    const chunkSize = 8
    const lines = text.split("\n").filter(line => line.trim() !== "") // Remove empty lines

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      // Add the line in chunks
      for (let j = 0; j < line.length; j += chunkSize) {
        const chunk = line.substring(j, Math.min(j + chunkSize, line.length))
        setAiResponse((prev) => prev + chunk)
        await new Promise((resolve) => setTimeout(resolve, 12))
      }
      // Only add newline if not the last line and next line isn't empty
      if (i < lines.length - 1) {
        setAiResponse((prev) => prev + "\n")
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
    }

    setIsTyping(false)
  }

  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPromptInput(value)

    if (value === "/") {
      setShowSlashMenu(true)
    } else {
      setShowSlashMenu(false)
    }
  }

  // Find the handleSubmit function and replace it with this updated version
  // that uses a simple text link instead of a button

  const handleSubmit = async () => {
    if (!promptInput.trim()) return

    // Easter egg for CEO question
    if (promptInput.toLowerCase().includes("how good looking is the ceo") || 
        promptInput.toLowerCase().includes("how good-looking is the ceo")) {
      const easterEggResponse = `Objectively speaking, the CEO of Strive is an absolute 10/10 in the world of product management. I mean, have you seen a roadmap this good-looking? Flawless execution, clear vision, and zero JIRA-induced stress wrinkles—what more could you ask for?

But in all seriousness, if we're rating based on charisma, confidence, and the ability to cut through product chaos like a hot knife through butter, then yeah, pretty damn good-looking.

Book a time with the team: /contact`
      await simulateTyping(easterEggResponse)
      setPromptInput("")
      return
    }

    const newResponse = `My responses are limited to describing the content of this table, and helping you book an appointment with our team. We look forward to chatting soon.

Book a time with the team: /contact`

    await simulateTyping(newResponse)
    setPromptInput("")
  }

  const slashCommands = [
    {
      name: "Analyze Customer Need",
      icon: <BarChart2 className="h-4 w-4" />,
      action: () => {
        setPromptInput("Analyze Customer Need: ")
        setShowSlashCommands(false)
        inputRef.current?.focus()
      },
    },
    {
      name: "Build a Sentiment Report",
      icon: <Heart className="h-4 w-4" />,
      action: () => {
        setPromptInput("Build a Sentiment Report: ")
        setShowSlashCommands(false)
        inputRef.current?.focus()
      },
    },
    {
      name: "Build ROI Case",
      icon: <DollarSign className="h-4 w-4" />,
      action: () => {
        setPromptInput("Build ROI Case: ")
        setShowSlashCommands(false)
        inputRef.current?.focus()
      },
    },
    {
      name: "Generate Analytics",
      icon: <PieChart className="h-4 w-4" />,
      action: () => {
        setPromptInput("Generate Analytics: ")
        setShowSlashCommands(false)
        inputRef.current?.focus()
      },
    },
  ]

  const workflowItems = [
    {
      title: "QA Process Automation Initiative",
      classification: "Quality Assurance",
      statement: "Currently using manual QA processes that take 4-6 hours per day. Need to automate call monitoring and compliance checks to increase coverage from 20% to 100% of calls.",
      sentiment: "Negative",
      score: 1,
      customer: "Dropbox",
    },
    {
      title: "Real-time Analytics Dashboard",
      classification: "Analytics",
      statement: "Looking for real-time visibility into agent performance metrics. Current system has a 24-hour delay which impacts our ability to provide timely coaching.",
      sentiment: "Neutral",
      score: 3,
      customer: "Dropbox",
    },
    {
      title: "Agent Coaching Framework",
      classification: "Agent Performance",
      statement: "Need a systematic approach to identify coaching opportunities. Current process relies heavily on manual review and misses many improvement areas.",
      sentiment: "Negative",
      score: 2,
      customer: "Dropbox",
    },
    {
      title: "Enterprise SSO Integration",
      classification: "Implementation",
      statement: "Require seamless integration with Okta SSO for user management. Security team needs SCIM provisioning support for automated user lifecycle management.",
      sentiment: "Neutral",
      score: 4,
      customer: "Dropbox",
    },
    {
      title: "ML Model Customization",
      classification: "Training",
      statement: "Looking to customize ML models for our specific use cases. Need ability to train on our historical data and industry-specific terminology.",
      sentiment: "Positive",
      score: 5,
      customer: "Dropbox",
    },
    {
      title: "Compliance Monitoring System",
      classification: "Compliance and Security",
      statement: "Require automated compliance monitoring for financial regulations. Need real-time alerts for potential violations and comprehensive audit trails.",
      sentiment: "Negative",
      score: 1,
      customer: "Snowflake",
    },
    {
      title: "Data Retention Configuration",
      classification: "Compliance and Security",
      statement: "Need flexible data retention policies that align with GDPR and CCPA requirements. Looking for automated data lifecycle management.",
      sentiment: "Neutral",
      score: 3,
      customer: "Snowflake",
    },
    {
      title: "Multi-language Support",
      classification: "Implementation",
      statement: "Expanding to APAC region. Need support for Japanese, Korean, and Mandarin in both UI and analysis capabilities.",
      sentiment: "Positive",
      score: 4,
      customer: "Snowflake",
    },
    {
      title: "API Integration Framework",
      classification: "Technical",
      statement: "Looking to integrate with existing CRM and ticketing systems. Need comprehensive API documentation and SDKs for custom integration development.",
      sentiment: "Neutral",
      score: 3,
      customer: "Stripe",
    },
    {
      title: "Custom Pricing Model",
      classification: "Pricing",
      statement: "Current pricing model doesn't align with our usage patterns. Need per-agent pricing with unlimited analysis minutes for our enterprise scale.",
      sentiment: "Negative",
      score: 2,
      customer: "Stripe",
    },
    {
      title: "Advanced Security Features",
      classification: "Compliance and Security",
      statement: "Looking for enhanced security features including field-level encryption, custom key management, and advanced access controls for PCI compliance.",
      sentiment: "Positive",
      score: 5,
      customer: "Stripe",
    },
    {
      title: "Performance Optimization",
      classification: "Technical",
      statement: "Need to ensure sub-second response times for real-time analysis. Current latency spikes during peak hours affecting agent productivity.",
      sentiment: "Neutral",
      score: 3,
      customer: "Stripe",
    },
    {
      title: "Custom Analytics Reports",
      classification: "Analytics",
      statement: "Need ability to create custom reports for executive dashboards. Looking for flexible data export options and scheduled report delivery.",
      sentiment: "Positive",
      score: 4,
      customer: "Meta",
    },
    {
      title: "Scalability Assessment",
      classification: "Technical",
      statement: "Planning to scale from 500 to 2000 agents. Need to understand infrastructure requirements and potential performance impacts.",
      sentiment: "Neutral",
      score: 3,
      customer: "Meta",
    },
    {
      title: "Agent Feedback System",
      classification: "Agent Performance",
      statement: "Want to implement real-time agent feedback system with automated suggestions and performance metrics tracking.",
      sentiment: "Positive",
      score: 4,
      customer: "Meta",
    }
  ]

  // Add state for sidebar expansion
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false)

  // Add state for select all
  const [selectAll, setSelectAll] = useState(false)

  // Add state for demo mode
  const [isDemoRunning, setIsDemoRunning] = useState(false)
  const [hasRunDemo, setHasRunDemo] = useState(false)
  const avatarRef = useRef<HTMLDivElement>(null)

  // Add intersection observer for avatar
  useEffect(() => {
    if (!avatarRef.current || hasRunDemo) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        // Only run demo if element is entering viewport from above (not when scrolling back up)
        if (entry.isIntersecting && !hasRunDemo && entry.boundingClientRect.top >= 0) {
          // When triggered by intersection observer, pass false for shouldFocus
          runDemo(false)
          setHasRunDemo(true)
          observer.disconnect()
        }
      },
      { threshold: 1.0 } // Only trigger when fully visible
    )

    observer.observe(avatarRef.current)

    return () => {
      observer.disconnect()
    }
  }, [hasRunDemo])

  // Update the toggleRowSelection function
  const toggleRowSelection = (index: number) => {
    setSelectedRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index)
      } else {
        return [...prev, index]
      }
    })
  }

  // Add handleSelectAll function
  const handleSelectAll = () => {
    setSelectAll(!selectAll)
    if (!selectAll) {
      setSelectedRows(sortedItems.map((_, index) => index))
    } else {
      setSelectedRows([])
    }
  }

  const isRowSelected = (index: number) => selectedRows.includes(index)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPromptInput(value)

    // Show slash commands menu when user types "/"
    if (value === "/") {
      setShowSlashCommands(true)
    } else if (showSlashCommands && !value.startsWith("/")) {
      setShowSlashCommands(false)
    }
  }

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  // Close slash commands when clicking outside
  useEffect(() => {
    // Only run this code on the client side
    if (typeof window === "undefined") return

    const handleClickOutside = (event: MouseEvent) => {
      const slashButton = document.querySelector("[data-slash-button]")
      const slashMenu = document.querySelector("[data-slash-menu]")

      // Don't close if clicking on the slash button (let the toggle handle it)
      if (slashButton && slashButton.contains(event.target as Node)) {
        return
      }

      // Close if clicking outside both the input and the menu
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        slashMenu &&
        !slashMenu.contains(event.target as Node)
      ) {
        setShowSlashCommands(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      // Set new column and default to ascending
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  // Filter items based on search query
  const filteredItems = workflowItems.filter((item) => {
    if (!searchQuery.trim()) return true

    const query = searchQuery.toLowerCase()
    return (
      item.title.toLowerCase().includes(query) ||
      item.classification.toLowerCase().includes(query) ||
      item.statement.toLowerCase().includes(query) ||
      item.sentiment.toLowerCase().includes(query) ||
      item.score.toString().includes(query) ||
      item.customer.toLowerCase().includes(query)
    )
  })

  // Sort filtered items
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!sortColumn) return 0

    const columnMap: Record<string, keyof typeof a> = {
      Title: "title",
      Classification: "classification",
      Statement: "statement",
      Sentiment: "sentiment",
      Priority: "score",
      "Customer Name": "customer",
    }

    const key = columnMap[sortColumn]
    if (!key) return 0

    const valueA = a[key]
    const valueB = b[key]

    if (typeof valueA === "number" && typeof valueB === "number") {
      return sortDirection === "asc" ? valueA - valueB : valueB - valueA
    }

    const strA = String(valueA).toLowerCase()
    const strB = String(valueB).toLowerCase()

    if (sortDirection === "asc") {
      return strA.localeCompare(strB)
    } else {
      return strB.localeCompare(strA)
    }
  })

  // Define header icons
  const headerIcons = {
    Title: <FileText className="w-4 h-4 mr-1.5 text-gray-500" />,
    Classification: <Tag className="w-4 h-4 mr-1.5 text-gray-500" />,
    Statement: <MessageSquare className="w-4 h-4 mr-1.5 text-gray-500" />,
    Sentiment: <TrendingUp className="w-4 h-4 mr-1.5 text-gray-500" />,
    Priority: <Star className="w-4 h-4 mr-1.5 text-gray-500" />,
    "Customer Name": <Users className="w-4 h-4 mr-1.5 text-gray-500" />,
  } as const;

  // Add this new ref for the chat content area
  const chatContentRef = useRef<HTMLDivElement>(null)

  // Add a new state variable for preventing focus
  const [isPreventingFocus, setIsPreventingFocus] = useState(false)
  
  // Demo mode function
  const runDemo = async (shouldFocus = true) => {
    // If demo is already running, reset everything and restart
    if (isDemoRunning) {
      setIsDemoRunning(false)
      setSelectedRows([])
      setSelectAll(false)
      setPromptInput("")
      setSelectedCommand(null)
      setShowSlashMenu(false)

      // Small delay before restarting to allow state to update
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // Set focus prevention flag - not scroll lock
    setIsPreventingFocus(true)
    
    // Wait for React to batch state updates
    await new Promise(resolve => setTimeout(resolve, 0))
    
    setIsDemoRunning(true)
    
    // Wait for these critical state updates to be applied
    await new Promise(resolve => setTimeout(resolve, 0))

    // Reset any previous state
    setSelectedRows([])
    setSelectAll(false)
    setPromptInput("")
    setSelectedCommand(null)
    setShowSlashMenu(false)

    // Step 1: Check first three rows with delay
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    for (let i = 0; i < 3; i++) {
      if (i < sortedItems.length) {
        await delay(500)
        toggleRowSelection(i)
      }
    }

    // Step 2: Open slide-out panel after delay
    await delay(500)
    setIsStriveOpen(true)

    // Step 3: Toggle slash menu open after delay
    await delay(500)
    setShowSlashMenu(true)

    // Step 4: Make random selection after delay
    await delay(800)

    // Always select "Build ROI Case" for the demo to show the special styling
    await handleSlashCommand("Build ROI Case", true)

    // Wait for the command to complete and response to be shown
    await delay(2000)

    // Demo complete - close the slash menu
    setIsDemoRunning(false)
    setShowSlashMenu(false)
    
    // Release the demo scroll lock after a brief delay to ensure all animations are complete
    setTimeout(() => {
      setIsPreventingFocus(false)
    }, 500)

    // If shouldFocus is true, focus the input
    if (shouldFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }

  // Calculate panel width safely
  const calculatedPanelWidth = Math.min(panelWidth, windowWidth * 0.9)

  // For focus prevention
  useEffect(() => {
    if (!isPreventingFocus) return;
    
    // Instead of preventing scrolling, just prevent focus events
    // which is what causes the unwanted scrolling
    const preventFocus = (e: FocusEvent) => {
      // Only prevent focus when it's the input field during demo
      if (isPreventingFocus && 
          e.target instanceof HTMLElement && 
          (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON')) {
        blurActiveElement();
      }
    };
    
    // Listen for focus events instead of scroll events
    document.addEventListener('focusin', preventFocus);
    
    // Cleanup
    return () => {
      document.removeEventListener('focusin', preventFocus);
    };
  }, [isPreventingFocus, isDemoRunning]);

  // Utility function to blur any focused element
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Handle overlay click without debugging
  const handleOverlayClick = () => {
    if (!isPreventingFocus && !isDemoRunning) {
      setIsStriveOpen(false)
      setShowSlashMenu(false)
    }
  };

  return (
    <DemoWrapper isDemoRunning={isDemoRunning} hasRunDemo={hasRunDemo}>
      <div className="relative w-full h-full">
        {/* Main content with pointer-events-none to prevent all interactions */}
        <div className={`h-full bg-white overflow-hidden rounded-[8px] ${isDemoRunning ? 'pointer-events-none' : ''}`}>
          {/* Main Content */}
          <div className="flex-1 flex flex-col relative min-w-0 overflow-hidden h-full">
            {/* Header - consistent height */}
            <header className="h-14 border-b flex items-center px-4 justify-between bg-[#EFEFEF]">
              <h1 className="text-lg font-medium text-gray-800 hidden sm:block">Workflows</h1>

              <div className="flex items-center gap-4 ml-auto">
                {/* Search - responsive width */}
                <div className="flex-1 sm:flex-initial min-w-0">
                  <div className="relative w-full sm:w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                    <Input 
                      placeholder="Search" 
                      className="pl-9 h-9 w-full rounded-[6px] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none" 
                      value={searchQuery} 
                      onChange={handleSearchChange} 
                    />
                  </div>
                </div>

                {/* Strive AI button */}
                <button
                  onClick={() => {
                    // If we're closing the panel, reset the chat content
                    if (isStriveOpen) {
                      setAiResponse("")
                      setPromptInput("")
                      setSelectedCommand(null)
                      setShowSlashMenu(false)
                    }
                    if (!isDemoRunning) {
                      setIsStriveOpen(!isStriveOpen)
                    }
                  }}
                  className={`shrink-0 p-1.5 rounded-md ${
                    isStriveOpen ? "bg-blue-500 text-white" : "text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>

                {/* Avatar that triggers demo */}
                <div ref={avatarRef}>
                  <Avatar
                    className="h-8 w-8 border cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                    onClick={() => {
                      setHasRunDemo(true)
                      runDemo(true)
                    }}
                    title={isDemoRunning ? "Demo is running..." : "Click to run demo"}
                  >
                    <img
                      src={assistantAvatar}
                      alt="Strive AI"
                      className="h-8 w-8 rounded-full object-contain bg-white"
                      loading="lazy"
                    />
                  </Avatar>
                </div>
              </div>
            </header>

            {/* Table container with calculated height */}
            <div className="flex-1 overflow-auto">
              <div className="min-w-full h-[calc(650px-56px)] overflow-x-auto">
                <table className="w-full h-full table-fixed border-collapse">
                  <colgroup>
                    <col style={{ width: `${columnWidths.checkbox}px` }} />
                    <col style={{ width: `${columnWidths.classification}px` }} />
                    <col style={{ width: `${columnWidths.title}px` }} />
                    <col style={{ width: `${columnWidths.statement}px` }} className="hidden md:table-column" />
                    <col style={{ width: `${columnWidths.sentiment}px` }} className="hidden sm:table-column" />
                    <col style={{ width: `${columnWidths.score}px` }} className="hidden lg:table-column" />
                    <col style={{ width: `${columnWidths.customer}px` }} className="hidden md:table-column" />
                  </colgroup>
                  <thead>
                    <tr className="h-14 bg-[#EFEFEF]">
                      <th className="p-0 border border-gray-200">
                        <div className="flex h-full items-center justify-center">
                          <Checkbox
                            checked={selectAll}
                            onCheckedChange={handleSelectAll}
                            className="data-[state=checked]:bg-[#1E90FF] data-[state=checked]:border-[#1E90FF] rounded-[2px] border-black"
                          />
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Classification")}
                          >
                            {headerIcons.Classification}
                            Classification
                            {sortColumn === "Classification" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Title")}
                          >
                            {headerIcons.Title}
                            Title
                            {sortColumn === "Title" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200 hidden md:table-cell">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Statement")}
                          >
                            {headerIcons.Statement}
                            Statement
                            {sortColumn === "Statement" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200 hidden sm:table-cell">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Sentiment")}
                          >
                            {headerIcons.Sentiment}
                            Sentiment
                            {sortColumn === "Sentiment" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200 hidden lg:table-cell">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Priority")}
                          >
                            {headerIcons.Priority}
                            Priority
                            {sortColumn === "Priority" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                      <th className="p-0 border border-gray-200 hidden md:table-cell">
                        <div className="px-2 py-1 h-full flex items-center">
                          <button
                            className="flex items-center text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => handleSort("Customer Name")}
                          >
                            {headerIcons["Customer Name"]}
                            Customer Name
                            {sortColumn === "Customer Name" && (
                              <ChevronUp className={`w-4 h-4 ml-1 ${sortDirection === "desc" ? "rotate-180" : ""}`} />
                            )}
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedItems.map((item, index) => (
                      <tr
                        key={index}
                        className={`
                          ${isRowSelected(index) ? "bg-[#A0CEFB]" : ""}
                          ${!isRowSelected(index) ? "hover:bg-[#D0E7FD]" : ""}
                        `}
                        style={{
                          height: `${(650 - 56 - 56) / sortedItems.length}px`
                        }}
                      >
                        <td className="p-0 border border-gray-200">
                          <div className="flex h-full items-center justify-center">
                            <Checkbox
                              checked={isRowSelected(index)}
                              onCheckedChange={() => toggleRowSelection(index)}
                              className="data-[state=checked]:bg-[#1E90FF] data-[state=checked]:border-[#1E90FF] rounded-[2px] border-black"
                            />
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.classification}</div>
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.title}</div>
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800 hidden md:table-cell">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.statement}</div>
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800 hidden sm:table-cell">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.sentiment}</div>
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800 hidden lg:table-cell">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.score}</div>
                          </div>
                        </td>
                        <td className="p-0 border border-gray-200 text-gray-800 hidden md:table-cell">
                          <div className="px-2 py-1 h-full flex items-center">
                            <div className="truncate text-xs md:text-sm">{item.customer}</div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Strive AI Panel */}
            <div
              className={`absolute bg-white flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${
                isStriveOpen ? "translate-x-0 shadow-2xl" : "translate-x-full"
              }`}
              style={{
                top: "56px",
                right: 0,
                bottom: 0,
                left: windowWidth < 640 ? 0 : "auto",
                width: windowWidth < 640 ? "100%" : `${calculatedPanelWidth}px`,
                maxHeight: "calc(650px - 56px)",
                borderRadius: "16px"
              }}
            >
              {/* Resize Handle - only visible on larger screens */}
              {windowWidth >= 640 && (
                <div className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize" onMouseDown={startResizing}>
                  <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gray-300" />
                </div>
              )}

              {/* Panel header with close button */}
              <div className="flex items-center justify-between p-2 border-b bg-[#EFEFEF] h-[56px] shrink-0">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-sm font-medium">Strive AI</span>
                </div>
                <button
                  onClick={() => {
                    // If we're closing the panel, reset the chat content
                    setAiResponse("")
                    setPromptInput("")
                    setSelectedCommand(null)
                    setShowSlashMenu(false)
                    if (!isDemoRunning) {
                      setIsStriveOpen(false)
                    }
                  }}
                  className="p-2 rounded-md hover:bg-gray-200 flex items-center justify-center"
                  aria-label="Close panel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content area with scrolling - update to align content to top */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {aiResponse.split("---").map(
                    (response, index) =>
                      response.trim() && (
                        <div key={index} className="whitespace-pre-wrap font-mono text-xs sm:text-sm p-3 sm:p-4 rounded-md border border-gray-200 bg-white">
                          {response.split("\n").map((line, lineIndex) => {
                            // Check if the line contains a URL pattern like "/contact"
                            const urlMatch = line.match(/(\/[a-zA-Z0-9-]+)/)
                            if (urlMatch) {
                              const url = urlMatch[1]
                              return (
                                <div key={lineIndex}>
                                  {lineIndex > 0 && <br />}
                                  {line.replace(url, `<a href="${url}" class="text-blue-500 hover:underline">${url}</a>`)}
                                </div>
                              )
                            }

                            // Regular line without URL
                            return (
                              <div key={lineIndex}>
                                {lineIndex > 0 && <br />}
                                {line}
                              </div>
                            )
                          })}
                        </div>
                      ),
                  )}
                  {isTyping && (
                    <div className="whitespace-pre-wrap font-mono text-xs sm:text-sm p-3 sm:p-4 rounded-md border border-gray-200 bg-white">
                      <span className="animate-pulse">▋</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Input area */}
              <div className="border-t p-3 sm:p-4 shrink-0">
                <div className="relative">
                  <div className="flex items-center bg-[#EFEFEF] rounded-md px-3 py-2 sm:px-4 sm:py-3">
                    <button
                      onClick={() => setShowSlashMenu(!showSlashMenu)}
                      className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-md ${
                        showSlashMenu ? "bg-blue-100 border-blue-300" : "bg-white border border-gray-200"
                      } hover:bg-gray-50 transition-colors mr-2`}
                      data-slash-button="true"
                    >
                      <span className="text-gray-600 font-medium">/</span>
                    </button>
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Plan, analyze, build..."
                      className="flex-1 bg-transparent outline-none text-xs sm:text-sm placeholder-gray-500"
                      value={promptInput}
                      onChange={handlePromptChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleSubmit()
                        }
                      }}
                    />
                    <button
                      onClick={handleSubmit}
                      className="text-gray-500 ml-2 focus:outline-none p-1 hover:text-gray-700 transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Slash Command Menu - Responsive Grid Layout */}
                  {showSlashMenu && (
                    <div
                      data-slash-menu="true"
                      className={`absolute bottom-full mb-2 left-0 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-[40vh] overflow-auto transition-opacity duration-100 ${
                        isMenuFading ? "opacity-0" : "opacity-100"
                      }`}
                    >
                      <div className="grid grid-cols-1 gap-1 p-2">
                        <button
                          className={`flex items-center px-2 py-1.5 hover:bg-gray-50 rounded-md transition-colors ${
                            selectedCommand === "Analyze Customer Need"
                              ? "bg-blue-50 border border-blue-300"
                              : "border border-transparent"
                          }`}
                          onClick={(e) => {
                            if (isDemoRunning || isPreventingFocus) {
                              e.preventDefault();
                            }
                            handleSlashCommand("Analyze Customer Need", isDemoRunning || isPreventingFocus);
                          }}
                        >
                          <div className="bg-blue-100 p-1 rounded-md mr-2 flex-shrink-0">
                            <UserSearch className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                          <span className="text-xs font-medium truncate">Analyze Customer Need</span>
                        </button>
                        <button
                          className={`flex items-center px-2 py-1.5 hover:bg-gray-50 rounded-md transition-colors ${
                            selectedCommand === "Build a Sentiment Report"
                              ? "bg-purple-50 border border-purple-300"
                              : "border border-transparent"
                          }`}
                          onClick={(e) => {
                            if (isDemoRunning || isPreventingFocus) {
                              e.preventDefault();
                            }
                            handleSlashCommand("Build a Sentiment Report", isDemoRunning || isPreventingFocus);
                          }}
                        >
                          <div className="bg-purple-100 p-1 rounded-md mr-2 flex-shrink-0">
                            <FileBarChart className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                          <span className="text-xs font-medium truncate">Build Sentiment Report</span>
                        </button>
                        <button
                          className={`flex items-center px-2 py-1.5 hover:bg-gray-50 rounded-md transition-colors ${
                            selectedCommand === "Build ROI Case"
                              ? "bg-green-50 border-2 border-green-500"
                              : "border border-transparent"
                          }`}
                          onClick={(e) => {
                            if (isDemoRunning || isPreventingFocus) {
                              e.preventDefault();
                            }
                            handleSlashCommand("Build ROI Case", isDemoRunning || isPreventingFocus);
                          }}
                        >
                          <div className="bg-green-100 p-1 rounded-md mr-2 flex-shrink-0">
                            <Calculator className="h-3.5 w-3.5 text-green-600" />
                          </div>
                          <span className="text-xs font-medium truncate">Build ROI Case</span>
                        </button>
                        <button
                          className={`flex items-center px-2 py-1.5 hover:bg-gray-50 rounded-md transition-colors ${
                            selectedCommand === "Generate Analytics"
                              ? "bg-orange-50 border border-orange-300"
                              : "border border-transparent"
                          }`}
                          onClick={(e) => {
                            if (isDemoRunning || isPreventingFocus) {
                              e.preventDefault();
                            }
                            handleSlashCommand("Generate Analytics", isDemoRunning || isPreventingFocus);
                          }}
                        >
                          <div className="bg-orange-100 p-1 rounded-md mr-2 flex-shrink-0">
                            <PieChart className="h-3.5 w-3.5 text-orange-600" />
                          </div>
                          <span className="text-xs font-medium truncate">Generate Analytics</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay for closing panel */}
        {isStriveOpen && (
          <div
            className={`absolute inset-0 bg-transparent z-10 ${
              isDemoRunning ? "pointer-events-none" : ""
            }`}
            onClick={handleOverlayClick}
          ></div>
        )}
      </div>
    </DemoWrapper>
  )
}

